// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import { format } from 'date-fns';
// Migrated: Using native Date instead of Firebase Timestamp
import { useSettingsStore as useCoreSettingsStore } from '@flows.cash/core';

// Define a Timestamp interface for backward compatibility
interface Timestamp {
  toDate(): Date;
}

// Define format options interface
interface FormatOptions {
  dateFormat?: string;
  timeFormat?: string;
}

export const formatDateTime = (
  date: Date | Timestamp | string | number | null,
  options?: FormatOptions
): string => {
  if (!date) return '';

  // Get default date and time formats from the core settings store
  const coreSettings = useCoreSettingsStore.getState();
  const dateFormat = (options?.dateFormat || coreSettings?.settings?.date_format || 'MMM d, yyyy')
    .replace('YYYY', 'yyyy')
    .replace('DD', 'dd');
  const timeFormat = (options?.timeFormat || coreSettings?.settings?.time_format || 'hh:mm a')
    .replace('YYYY', 'yyyy')
    .replace('DD', 'dd');

  let dateObj: Date;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'object' && date !== null && 'toDate' in date) {
    // Handle Timestamp objects
    dateObj = (date as Timestamp).toDate();
  } else if (typeof date === 'string' || typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    return '';
  }

  try {
    return `${format(dateObj, dateFormat)} ${format(dateObj, timeFormat)}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    // Fallback to a safe format
    return `${format(dateObj, 'MMM d, yyyy')} ${format(dateObj, 'hh:mm a')}`;
  }
};

export const formatDateOnly = (date: Date | Timestamp | string | number | null): string => {
  if (!date) return '';

  let dateObj: Date;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'object' && date !== null && 'toDate' in date) {
    // Handle Timestamp objects
    dateObj = (date as Timestamp).toDate();
  } else if (typeof date === 'string' || typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    return '';
  }

  try {
    return format(dateObj, 'PP');
  } catch (error) {
    console.error('Error formatting date:', error);
    return format(dateObj, 'MMM d, yyyy');
  }
};

export const formatTimeOnly = (date: Date | Timestamp | string | number | null): string => {
  if (!date) return '';

  let dateObj: Date;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'object' && date !== null && 'toDate' in date) {
    // Handle Timestamp objects
    dateObj = (date as Timestamp).toDate();
  } else if (typeof date === 'string' || typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    return '';
  }

  try {
    return format(dateObj, 'p');
  } catch (error) {
    console.error('Error formatting time:', error);
    return format(dateObj, 'hh:mm a');
  }
};

export const formatDateForInput = (date: Date | Timestamp | string | number | null): string => {
  if (!date) return '';

  let dateObj: Date;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'object' && date !== null && 'toDate' in date) {
    // Handle Timestamp objects
    dateObj = (date as Timestamp).toDate();
  } else if (typeof date === 'string' || typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    return '';
  }

  try {
    return format(dateObj, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
};

export const getDateRangeFilter = (dateRange: string, customRange?: { startDate: string; endDate: string }): Date[] => {
  const now = new Date();
  // Set to start of today
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  today.setHours(0, 0, 0, 0);

  const getEndOfDay = (date: Date): Date => {
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return end;
  };

  const getStartOfWeek = (): Date => {
    const start = new Date(today);
    start.setDate(today.getDate() - today.getDay()); // Go to Sunday
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getStartOfMonth = (): Date => {
    const start = new Date(today);
    start.setDate(1); // First day of current month
    start.setHours(0, 0, 0, 0);
    return start;
  };

  switch (dateRange) {
    case 'all':
      return []; // Return empty array for all time, meaning no date filtering
    case 'today':
      return [today, getEndOfDay(today)];
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return [yesterday, getEndOfDay(yesterday)];
    }
    case 'thisWeek':
      return [getStartOfWeek(), getEndOfDay(today)];
    case 'lastWeek': {
      const lastWeekEnd = new Date(getStartOfWeek());
      lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
      return [lastWeekStart, getEndOfDay(lastWeekEnd)];
    }
    case 'thisMonth':
      return [getStartOfMonth(), getEndOfDay(today)];
    case 'lastMonth': {
      const lastMonthEnd = new Date(getStartOfMonth());
      lastMonthEnd.setDate(lastMonthEnd.getDate() - 1);
      const lastMonthStart = new Date(lastMonthEnd);
      lastMonthStart.setDate(1);
      return [lastMonthStart, getEndOfDay(lastMonthEnd)];
    }
    case 'thisYear': {
      const yearStart = new Date(today.getFullYear(), 0, 1);
      return [yearStart, getEndOfDay(today)];
    }
    case 'lastYear': {
      const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
      const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
      return [lastYearStart, getEndOfDay(lastYearEnd)];
    }
    case 'custom':
      if (customRange?.startDate && customRange?.endDate) {
        const start = new Date(customRange.startDate);
        const end = new Date(customRange.endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return [start, end];
      }
      return [];
    default:
      return [];
  }
};

export const getDateGroupKey = (date: Date | Timestamp | string | number | null): string => {
  if (!date) return 'Unknown';

  let dateObj: Date;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'object' && date !== null && 'toDate' in date) {
    // Handle Timestamp objects
    dateObj = (date as Timestamp).toDate();
  } else if (typeof date === 'string' || typeof date === 'number') {
    dateObj = new Date(date);
  } else {
    return 'Unknown';
  }

  return format(dateObj, 'PP'); // Returns date in format like "Apr 29, 2023"
};
